import { useState, useEffect, Fragment, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon } from "components/Icons";
import { signal } from "@preact/signals-react";
import Dialog2 from "componentsv2/modal/Dialog";

import proxy from "api/proxy";
import { getStorage } from "store/local";

import {
  useMessage,
  MessageContainer,
} from "componentsv2/experimental/Message";
import BarcodeReader from "componentsv2/widgets/BarcodeReader";
import Button from "componentsv2/button/Button";
import ProductItemCard from "./ProductItemCard";
import useMainStore from "store/mainStore";
import requestView from "../ModelStockRequest";

const code = signal(null);
const receivedLog = signal([]);
const openModalPartial = signal(true);

function ValidateProducts(props) {
  const {
    messages,
    hide,
    success: showSuccess,
    warning,
    error: setError,
  } = useMessage();
  const { record, onClose } = props;
  const moves = record?.["outgoing_moves."];
  const { setRecord } = useMainStore();
  const [ctxView, setCtxView] = useState(null);
  const [comparate, setComparate] = useState(false);
  const [records, setRecords] = useState([]);
  const [dialogInfo, setDialogInfo] = useState(null);

  const _moves = useMemo(
    () =>
      moves?.map((item) => ({
        id: item.product.id,
        stockMoveId: item.id,
        name: item.product.name,
        code: item.product.code,
        price: item.product.list_price,
        uom:
          (item.product["default_uom.id"] ||
            item.product["default_uom."]?.id ||
            item.unit?.id) ??
          item.unit,
        quantity: item.quantity,
      })) ?? [],
    [moves],
  );

  useEffect(() => {
    requestView.ctxView(props).then(setCtxView);
  }, []);

  useEffect(() => {
    if (_moves.length) {
      setRecords(_moves.map((move) => ({ ...move, quantity: 0 })));
    }
  }, [_moves]);

  const updateQuantity = (_, value) => {
    setRecords((prev) => {
      const updated = [...prev];
      for (const key in value) {
        const idx = Number(key);
        const qty = Number(value[key]);
        if (updated[idx]) updated[idx].quantity = qty;
      }
      return updated;
    });
  };

  function compareLists() {
    if (!_moves?.length) {
      return false;
    }
    if (!records?.length) {
      return false;
    }

    if (_moves.length !== records.length) {
      return false;
    }

    for (const move of _moves) {
      const rec = records.find((r) => r.code === move.code);
      if (!rec) {
        return false;
      }

      if (Number(rec.quantity) !== Number(move.quantity)) {
        return false;
      }
    }

    return true;
  }

  const hasExcess = useMemo(() => {
    if (!_moves.length || !records.length) return false;
    return records.some((rec) => {
      const move = _moves.find((m) => m.code === rec.code);
      return move && Number(rec.quantity) > Number(move.quantity);
    });
  }, [_moves, records]);

  const fieldQuantity = {
    label: "stock.shipment.internal.quantity",
    name: "quantity",
    withChange: updateQuantity,
  };

  async function getData(codeValue) {
    if (!codeValue) return;

    const requestedProduct = _moves.find((item) => item.code === codeValue);

    if (!requestedProduct) {
      warning("Este producto no fue solicitado");
      return;
    }

    setRecords((prev) =>
      prev.map((item) =>
        item.code === codeValue
          ? { ...item, quantity: Number(item.quantity) + 1 }
          : item,
      ),
    );

    showSuccess(`Cantidad aumentada para ${requestedProduct.name}`);
    code.value = null;
  }

  async function handleValidate() {
    const success = compareLists();
    setComparate(success);

    if (success) {
      await proxy.buttonMethod({
        model: "stock.shipment.internal",
        method: "done",
        ids: [record.id],
      });

      setRecord(record.id, "stock.shipment.internal", ctxView);
      onClose();
    }
  }

  async function onSavePartial() {
    if (records.every((r) => r.quantity === 0)) {
      warning("No hay productos recibidos");
      return;
    }

    const movesPayload = _moves.map((move) => {
      const scanned = records.find((r) => r.code === move.code);
      return {
        id: move.stockMoveId,
        quantity: scanned ? Number(scanned.quantity) : 0,
      };
    });

    try {
      const { data, error } = await proxy.methodCall({
        model: "stock.shipment.internal",
        method: "receive_partial_shipment",
        args: [
          {
            id: String(record.id),
            moves: movesPayload,
          },
        ],
      });

      if (error) throw error;

      setRecords([]);
      receivedLog.value = [];
      setRecord(record.id, "stock.shipment.internal", ctxView);

      showSuccess(data.msg);

      if (data.shortage_number) {
        setDialogInfo(data);
      } else if (!data.open_modal) {
        onClose();
      }
    } catch (err) {
      const msg =
        err?.detail?.error ||
        err?.error ||
        err?.message ||
        err ||
        "Error desconocido";
      setError(msg);
    }
  }

  async function refundRequest() {
    try {
      const session = getStorage("ctxSession");

      const refundLines = _moves.map((move) => ({
        product: move.id,
        quantity: move.quantity,
        unit: move.uom,
        from_location: record.to_location?.id ?? record.to_location,
        to_location: record.from_location?.id ?? record.from_location,
      }));

      const { data: newShipment } = await proxy.create(
        "stock.shipment.internal",
        {
          from_location: record.to_location?.id ?? record.to_location,
          to_location: record.from_location?.id ?? record.from_location,
          requested_by: session?.user_employee,
          company: session?.company?.id ?? session?.company,
          effective_date: new Date().toISOString().split("T")[0],
          reference: `DEVOLUCIÓN - ${record.number}`,
          moves: [["create", refundLines]],
        },
        ["id", "number"],
      );

      if (newShipment?.id) {
        await proxy.buttonMethod({
          model: "stock.shipment.internal",
          method: "wait",
          ids: [newShipment.id],
        });
      }

      showSuccess(`Devolución ${newShipment.number} creada`);
    } catch (error) {
      setError("Error al crear devolución: " + error.message);
    }
  }

  async function handleExcessReceipt() {
    try {
      const movesPayload = _moves.map((move) => {
        const scanned = records.find((r) => r.code === move.code);
        return {
          id: move.stockMoveId,
          quantity: scanned ? Number(scanned.quantity) : 0,
        };
      });

      const { data, error } = await proxy.methodCall({
        model: "stock.shipment.internal",
        method: "receive_mixed_shipment",
        args: [
          {
            id: String(record.id),
            moves: movesPayload,
            note: "",
          },
        ],
      });

      if (error) throw error;

      setRecord(record.id, "stock.shipment.internal", ctxView);
      showSuccess(data.msg);

      if (data.note) {
        setDialogInfo(data);
      } else {
        onClose();
      }
    } catch (err) {
      const msg =
        err?.detail?.error ||
        err?.error ||
        err?.message ||
        err ||
        "Error desconocido";
      setError(msg);
    }
  }

  function onRemove(item) {
    setRecords((prev) =>
      prev.map((r) => (r.id === item.id ? { ...r, quantity: 0 } : r)),
    );
  }

  useEffect(() => {
    const success = compareLists();
    setComparate(success);

    if (success) {
      showSuccess("Todo coincide puedes continuar con el recibido");
    }
  }, [records]);

  return (
    <Fragment>
      <div className="py-10">
        <div className="grid grid-cols-2 space-x-8">
          <div>
            <h2 className="text-xl font-bold">Solicitados</h2>
            {_moves?.map((item, index) => (
              <ProductItemCard
                key={index}
                item={item}
                index={index}
                fieldQuantity={fieldQuantity}
                readOnly={true}
              />
            ))}
          </div>

          <div>
            <h2 className="text-xl font-bold">Ingresados</h2>
            {records.map((item, index) => (
              <ProductItemCard
                key={index}
                item={item}
                index={index}
                fieldQuantity={fieldQuantity}
                onRemove={() => onRemove(item)}
              />
            ))}
          </div>
        </div>

        <div className="w-full flex justify-center mt-5 space-x-3">
          <Button
            color="rose"
            size="w-max"
            onClick={onClose}
            content="CANCELAR"
          />
          {records.some((r) => r.quantity > 0) && !hasExcess && !comparate && (
            <Button
              color="yellow"
              size="w-max"
              onClick={onSavePartial}
              content="RECIBIR PARCIALMENTE"
            />
          )}
          {!hasExcess && comparate && (
            <Fragment>
              {/* <Button
                size="w-max"
                color="black"
                onClick={refundRequest}
                content="SOLICITAR DEVOLUCION"
              /> */}
              <Button
                color="green"
                size="w-max"
                onClick={handleValidate}
                content="CONFIRMAR RECIBIDOS"
              />
            </Fragment>
          )}
          {hasExcess && (
            <Button
              size="w-max"
              color="orange"
              onClick={handleExcessReceipt}
              content="RECIBIR Y AJUSTAR"
            />
          )}
        </div>

        <BarcodeReader data={props.data} handleReader={getData} />
      </div>

      <Transition.Root show={!!dialogInfo} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => {
            setDialogInfo(null);
            onClose();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[60]" />
          </Transition.Child>
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckIcon
                        className="h-6 w-6 text-green-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Envío procesado
                      </Dialog.Title>
                      <div className="mt-2 space-y-2">
                        {dialogInfo?.note && (
                          <p className="whitespace-pre-line text-sm text-gray-500">
                            {dialogInfo.note}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={() => {
                        setDialogInfo(null);
                        onClose();
                      }}
                    >
                      Aceptar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* <Dialog2
        openModal={openModalPartial}
        handleClose={() => (openModalPartial.value = false)}
        // title={"schedule.wizard_schedule.title"}
      >
        aca se abre la info de la nueva
      </Dialog2> */}

      <MessageContainer
        messages={messages}
        onMessageClose={hide}
        position="bottom-center"
      />
    </Fragment>
  );
}

export default ValidateProducts;
